<template>
  <div class="hms-page">
    <Navigation />

    <div class="container hms-page-body">
      <div class="d-flex align-items-center justify-content-between mb-3 hms-page-header">
        <div class="d-flex align-items-center gap-3">
          <a href="javascript:void(0)" @click="$router.push('/dashboard')" class="hms-back" title="Back">
            <i class="mdi mdi-arrow-left"></i>
          </a>
          <h4 class="hms-page-title m-0"><i class="mdi mdi-video-outline me-2"></i>Telehealth — Hub & Spoke</h4>
        </div>
        <div class="d-flex gap-2">
          <button class="hms-btn hms-btn-success" @click="openSchedule()"><i class="mdi mdi-plus"></i> Schedule</button>
          <button class="hms-btn hms-btn-blue" @click="loadSessions"><i class="mdi mdi-refresh"></i></button>
        </div>
      </div>

      <!-- KPI -->
      <div class="hms-stat-row">
        <div class="hms-stat"><div class="hms-stat-num hms-stat-info">{{ stats.today_total || 0 }}</div><div class="hms-stat-lbl">Today</div></div>
        <div class="hms-stat"><div class="hms-stat-num hms-stat-success">{{ stats.in_progress || 0 }}</div><div class="hms-stat-lbl">In progress</div></div>
        <div class="hms-stat"><div class="hms-stat-num hms-stat-warning">{{ stats.waiting || 0 }}</div><div class="hms-stat-lbl">Waiting room</div></div>
        <div class="hms-stat"><div class="hms-stat-num">{{ stats.upcoming || 0 }}</div><div class="hms-stat-lbl">Upcoming</div></div>
        <div class="hms-stat"><div class="hms-stat-num hms-stat-success">{{ stats.done_today || 0 }}</div><div class="hms-stat-lbl">Done today</div></div>
        <div class="hms-stat"><div class="hms-stat-num hms-stat-danger">{{ stats.emergencies || 0 }}</div><div class="hms-stat-lbl">Emergencies</div></div>
      </div>

      <!-- TABS -->
      <div class="hms-tab-row">
        <button class="hms-tab" :class="{ active: scope==='today' }" @click="scope='today'; loadSessions()"><i class="mdi mdi-calendar-today me-1"></i>Today</button>
        <button class="hms-tab" :class="{ active: scope==='upcoming' }" @click="scope='upcoming'; loadSessions()"><i class="mdi mdi-calendar-clock me-1"></i>Upcoming</button>
        <button class="hms-tab" :class="{ active: scope==='history' }" @click="scope='history'; loadSessions()"><i class="mdi mdi-history me-1"></i>History</button>
      </div>

      <!-- TABLE -->
      <table class="hms-table">
        <thead>
          <tr>
            <th>#</th><th>Patient</th><th>Specialty / Mode</th>
            <th>Provider</th><th>Spoke facility</th>
            <th>Scheduled</th><th>Urgency</th><th>Status</th>
            <th>Join</th><th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="s in sessions" :key="s.session_id" :class="rowClass(s)">
            <td><strong>{{ s.document_no }}</strong></td>
            <td>
              <div>{{ s.patient_name }}</div>
              <div class="hms-muted-sm">{{ s.patient_number }} · {{ s.sex }} · {{ ageYears(s.date_of_birth) }}y</div>
            </td>
            <td>
              <div><strong>{{ s.specialty || '—' }}</strong></div>
              <span class="hms-pill" :class="modePill(s.mode)"><i class="mdi" :class="modeIcon(s.mode)"></i> {{ s.mode }}</span>
            </td>
            <td>{{ s.provider_name || '—' }}<div class="hms-muted-sm">{{ s.provider_role || '' }}</div></td>
            <td>{{ s.requesting_facility || '—' }}<div v-if="s.requesting_facility_code" class="hms-muted-sm">KMHFL: {{ s.requesting_facility_code }}</div></td>
            <td>
              <div>{{ formatDt(s.scheduled_start) }}</div>
              <div v-if="s.actual_start" class="hms-muted-sm">Started {{ formatTime(s.actual_start) }}</div>
            </td>
            <td><span class="hms-pill" :class="urgencyPill(s.urgency)">{{ s.urgency }}</span></td>
            <td><span class="hms-pill" :class="statusPill(s.status)">{{ s.status }}</span></td>
            <td><code>{{ s.join_code }}</code></td>
            <td>
              <button class="hms-btn hms-btn-blue hms-btn-sm" @click="openConsult(s)">
                <i class="mdi mdi-video"></i>
                {{ s.status === 'scheduled' ? 'Join' : (s.status === 'in_progress' ? 'Resume' : 'Open') }}
              </button>
            </td>
          </tr>
          <tr v-if="!sessions.length"><td colspan="10" class="text-center hms-pad hms-muted">No sessions in this view.</td></tr>
        </tbody>
      </table>
    </div>

    <!-- SCHEDULE MODAL -->
    <div v-if="schedModal" class="hms-modal-bg" @click.self="schedModal=false">
      <div class="hms-modal" style="width:680px">
        <div class="hms-modal-head"><h5>Schedule telehealth session</h5><button class="hms-icon-btn" @click="schedModal=false"><i class="mdi mdi-close"></i></button></div>
        <div class="hms-modal-body">
          <div class="row g-2">
            <div class="col-4"><label class="hms-label">Patient ID *</label><input v-model.number="form.patient_id" type="number" class="hms-input" /></div>
            <div class="col-4"><label class="hms-label">Provider (staff ID)</label><input v-model.number="form.provider_staff_id" type="number" class="hms-input" /></div>
            <div class="col-4"><label class="hms-label">Specialty</label>
              <select v-model="form.specialty" class="hms-input">
                <option value="">—</option>
                <option>Internal Medicine</option><option>Paediatrics</option><option>OB/GYN</option>
                <option>Surgery</option><option>Psychiatry</option><option>Dermatology</option>
                <option>ENT</option><option>Ophthalmology</option><option>Cardiology</option>
                <option>Oncology</option><option>Nephrology</option><option>Endocrinology</option>
                <option>Family Medicine</option>
              </select>
            </div>
            <div class="col-4"><label class="hms-label">Mode</label>
              <select v-model="form.mode" class="hms-input">
                <option value="video">Video</option><option value="audio">Audio</option>
                <option value="chat">Chat</option><option value="store_forward">Store-and-forward</option>
              </select>
            </div>
            <div class="col-4"><label class="hms-label">Urgency</label>
              <select v-model="form.urgency" class="hms-input">
                <option value="routine">Routine</option><option value="urgent">Urgent</option><option value="emergency">Emergency</option>
              </select>
            </div>
            <div class="col-4 d-flex align-items-end"><label><input type="checkbox" v-model="form.consent_recorded" :true-value="1" :false-value="0" /> Consent recorded</label></div>
            <div class="col-6"><label class="hms-label">Scheduled start *</label><input v-model="form.scheduled_start" type="datetime-local" class="hms-input" /></div>
            <div class="col-6"><label class="hms-label">Scheduled end</label><input v-model="form.scheduled_end" type="datetime-local" class="hms-input" /></div>
            <div class="col-6"><label class="hms-label">Spoke facility (referring)</label><input v-model="form.requesting_facility" class="hms-input" placeholder="e.g. Mara Suswa Health Centre" /></div>
            <div class="col-3"><label class="hms-label">KMHFL code</label><input v-model="form.requesting_facility_code" class="hms-input" /></div>
            <div class="col-3"><label class="hms-label">Referring staff ID</label><input v-model.number="form.requesting_staff_id" type="number" class="hms-input" /></div>

            <div class="col-12 mt-2"><label class="hms-label">Chief complaint</label><textarea v-model="form.chief_complaint" class="hms-input" rows="2"></textarea></div>
            <div class="col-12"><label class="hms-label">Referral reason</label><textarea v-model="form.referral_reason" class="hms-input" rows="2"></textarea></div>
            <div class="col-12"><label class="hms-label">History summary</label><textarea v-model="form.history_summary" class="hms-input" rows="2"></textarea></div>

            <div class="col-12 mt-2 hms-section-lbl">Vitals at spoke (optional)</div>
            <div class="col-2"><label class="hms-label">Temp (°C)</label><input v-model.number="form.vital_temp" type="number" step="0.1" class="hms-input" /></div>
            <div class="col-2"><label class="hms-label">Pulse</label><input v-model.number="form.vital_pulse" type="number" class="hms-input" /></div>
            <div class="col-2"><label class="hms-label">Resp</label><input v-model.number="form.vital_resp" type="number" class="hms-input" /></div>
            <div class="col-2"><label class="hms-label">BP</label><input v-model="form.vital_bp" placeholder="120/80" class="hms-input" /></div>
            <div class="col-2"><label class="hms-label">SpO2 %</label><input v-model.number="form.vital_spo2" type="number" class="hms-input" /></div>
            <div class="col-2"><label class="hms-label">BGL</label><input v-model.number="form.vital_bgl" type="number" step="0.1" class="hms-input" /></div>
          </div>
        </div>
        <div class="hms-modal-foot">
          <button class="hms-btn hms-btn-secondary" @click="schedModal=false">Cancel</button>
          <button class="hms-btn hms-btn-success" :disabled="saving" @click="saveSchedule">{{ saving?'Scheduling…':'Schedule' }}</button>
        </div>
      </div>
    </div>

    <!-- CONSULTATION ROOM MODAL -->
    <div v-if="consultModal" class="hms-modal-bg" @click.self="consultModal=false">
      <div class="hms-modal hms-consult-modal">
        <div class="hms-modal-head">
          <div>
            <h5 class="m-0">Consultation Room — {{ active.document_no }}</h5>
            <div class="hms-muted-sm">{{ active.patient_name }} · {{ active.specialty }} · <span class="hms-pill" :class="statusPill(active.status)">{{ active.status }}</span></div>
          </div>
          <button class="hms-icon-btn" @click="consultModal=false"><i class="mdi mdi-close"></i></button>
        </div>
        <div class="hms-modal-body p-0">
          <div class="hms-consult-grid">
            <!-- LEFT: video stub + actions + clinical -->
            <div class="hms-consult-left">
              <div class="hms-video-stub">
                <i class="mdi mdi-video" style="font-size:64px"></i>
                <div>{{ active.mode === 'video' ? 'Video' : (active.mode === 'audio' ? 'Audio' : active.mode) }} session</div>
                <div class="hms-muted-sm">Join code: <code>{{ active.join_code }}</code></div>
                <div v-if="active.connection_quality" class="hms-muted-sm">Connection: <strong :class="qualityClass(active.connection_quality)">{{ active.connection_quality }}</strong></div>
              </div>

              <div class="d-flex gap-2 mt-2 flex-wrap">
                <button v-if="active.status==='scheduled'" class="hms-btn hms-btn-warning hms-btn-sm" @click="actOn('enter_waiting_room')"><i class="mdi mdi-account-clock"></i> Waiting room</button>
                <button v-if="['scheduled','waiting_room'].includes(active.status)" class="hms-btn hms-btn-success hms-btn-sm" @click="actOn('start')"><i class="mdi mdi-phone"></i> Start</button>
                <button v-if="active.status==='in_progress'" class="hms-btn hms-btn-success hms-btn-sm" @click="completeMode=true"><i class="mdi mdi-check-circle"></i> Complete</button>
                <button v-if="['scheduled','waiting_room'].includes(active.status)" class="hms-btn hms-btn-secondary hms-btn-sm" @click="actOn('no_show')"><i class="mdi mdi-account-off"></i> No-show</button>
                <button v-if="active.status!=='completed' && active.status!=='cancelled'" class="hms-btn hms-btn-secondary hms-btn-sm" @click="cancelSess"><i class="mdi mdi-close-circle"></i> Cancel</button>
              </div>

              <div v-if="active.status==='completed' || completeMode" class="hms-consult-section mt-3">
                <div class="hms-section-lbl">Clinical outcome</div>
                <div class="row g-2">
                  <div class="col-12"><label class="hms-label">Clinical notes</label><textarea v-model="complete.clinical_notes" class="hms-input" rows="2" :disabled="active.status==='completed'"></textarea></div>
                  <div class="col-6"><label class="hms-label">Impression</label><textarea v-model="complete.impression" class="hms-input" rows="2" :disabled="active.status==='completed'"></textarea></div>
                  <div class="col-6"><label class="hms-label">Plan</label><textarea v-model="complete.plan_text" class="hms-input" rows="2" :disabled="active.status==='completed'"></textarea></div>
                  <div class="col-6"><label class="hms-label">ICD-11 codes</label><input v-model="complete.icd11_codes" class="hms-input" :disabled="active.status==='completed'" /></div>
                  <div class="col-6"><label class="hms-label">Follow-up date</label><input v-model="complete.follow_up_date" type="date" class="hms-input" :disabled="active.status==='completed'" /></div>
                  <div class="col-12"><label class="hms-label">Prescription (handoff to spoke pharmacy)</label><textarea v-model="complete.prescription_text" class="hms-input" rows="2" :disabled="active.status==='completed'"></textarea></div>
                  <div class="col-12"><label class="hms-label">Investigations</label><textarea v-model="complete.investigations_text" class="hms-input" rows="2" :disabled="active.status==='completed'"></textarea></div>
                  <div class="col-4"><label class="hms-label">Connection</label>
                    <select v-model="complete.connection_quality" class="hms-input" :disabled="active.status==='completed'">
                      <option value="">—</option>
                      <option value="excellent">Excellent</option><option value="good">Good</option>
                      <option value="fair">Fair</option><option value="poor">Poor</option><option value="dropped">Dropped</option>
                    </select>
                  </div>
                  <div class="col-4 d-flex align-items-end"><label><input type="checkbox" v-model="complete.refer_in_person" :true-value="1" :false-value="0" :disabled="active.status==='completed'" /> Refer in person</label></div>
                  <div class="col-4"><label class="hms-label">Referral facility</label><input v-model="complete.referral_facility" class="hms-input" :disabled="active.status==='completed'" /></div>
                  <div class="col-12"><label class="hms-label">Technical notes</label><textarea v-model="complete.technical_notes" class="hms-input" rows="2" :disabled="active.status==='completed'"></textarea></div>
                </div>
                <div v-if="active.status!=='completed'" class="text-end mt-2">
                  <button class="hms-btn hms-btn-success" :disabled="saving" @click="finalizeComplete">{{ saving?'Finalising…':'Finalise consultation' }}</button>
                </div>
              </div>

              <div class="hms-consult-section mt-3">
                <div class="hms-section-lbl">Patient snapshot</div>
                <div class="row g-2">
                  <div class="col-6"><strong>Patient:</strong> {{ active.patient_name }} ({{ active.patient_number }})</div>
                  <div class="col-6"><strong>Sex / Age:</strong> {{ active.sex }} · {{ ageYears(active.date_of_birth) }}y</div>
                  <div class="col-6"><strong>Phone:</strong> {{ active.patient_phone || '—' }}</div>
                  <div class="col-6"><strong>Spoke:</strong> {{ active.requesting_facility || '—' }}</div>
                  <div v-if="active.chief_complaint" class="col-12"><strong>CC:</strong> {{ active.chief_complaint }}</div>
                  <div v-if="active.history_summary" class="col-12"><strong>History:</strong> {{ active.history_summary }}</div>
                  <div v-if="hasVitals" class="col-12 hms-muted-sm">
                    Vitals: T {{ active.vital_temp || '—' }}°C · HR {{ active.vital_pulse || '—' }} · RR {{ active.vital_resp || '—' }} · BP {{ active.vital_bp || '—' }} · SpO2 {{ active.vital_spo2 || '—' }}% · BGL {{ active.vital_bgl || '—' }}
                  </div>
                </div>
              </div>
            </div>

            <!-- RIGHT: chat -->
            <div class="hms-consult-right">
              <div class="hms-section-lbl">Chat / log</div>
              <div ref="chatBox" class="hms-chat-box">
                <div v-for="m in messages" :key="m.message_id" class="hms-chat-msg" :class="'sender-'+m.sender_type">
                  <div class="hms-chat-meta">
                    <strong>{{ m.sender_name || senderLabel(m.sender_type) }}</strong>
                    · {{ formatTime(m.sent_at) }}
                  </div>
                  <div class="hms-chat-body">{{ m.message }}</div>
                </div>
                <div v-if="!messages.length" class="hms-muted text-center" style="padding:20px">No messages yet.</div>
              </div>
              <div class="hms-chat-input">
                <select v-model="chatSender" class="hms-input" style="max-width:140px">
                  <option value="provider">Provider</option>
                  <option value="spoke_clinician">Spoke</option>
                  <option value="patient">Patient</option>
                </select>
                <input v-model="chatMsg" class="hms-input" placeholder="Type a message…" @keyup.enter="sendChat" />
                <button class="hms-btn hms-btn-blue" :disabled="!chatMsg" @click="sendChat"><i class="mdi mdi-send"></i></button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import Navigation from "@/components/hms-auth/Navigation.vue";
import { executeJson, hms } from "@/api";

export default {
  name: "TelehealthConsole",
  components: { Navigation },
  data() {
    return {
      scope: 'today', saving: false,
      sessions: [], stats: {},
      schedModal: false, form: this.blankForm(),
      consultModal: false, completeMode: false,
      active: {}, messages: [],
      complete: this.blankComplete(),
      chatSender: 'provider', chatMsg: '',
      pollTimer: null
    };
  },
  computed: {
    hasVitals() {
      const a = this.active;
      return a.vital_temp || a.vital_pulse || a.vital_resp || a.vital_bp || a.vital_spo2 || a.vital_bgl;
    }
  },
  mounted() { this.loadSessions(); },
  beforeUnmount() { if (this.pollTimer) clearInterval(this.pollTimer); },
  methods: {
    blankForm() {
      const now = new Date();
      now.setMinutes(now.getMinutes() + 30);
      return {
        patient_id: null, provider_staff_id: null, requesting_staff_id: null,
        requesting_facility: '', requesting_facility_code: '',
        specialty: 'Internal Medicine', mode: 'video', urgency: 'routine',
        scheduled_start: now.toISOString().slice(0, 16), scheduled_end: '',
        chief_complaint: '', referral_reason: '', history_summary: '',
        vital_temp: null, vital_pulse: null, vital_resp: null, vital_bp: '', vital_spo2: null, vital_bgl: null,
        consent_recorded: 1
      };
    },
    blankComplete() {
      return {
        clinical_notes: '', impression: '', plan_text: '', icd11_codes: '',
        prescription_text: '', investigations_text: '', follow_up_date: '',
        refer_in_person: 0, referral_facility: '',
        connection_quality: '', technical_notes: ''
      };
    },

    async loadSessions() {
      try {
        const r = await executeJson({ function: 'getTelehealthSessions', scope: this.scope }, hms);
        if (r.data && r.data.success) {
          const d = r.data.data || {};
          this.sessions = d.Sessions || [];
          this.stats = d.Stats || {};
        }
      } catch (e) { console.error(e); }
    },

    openSchedule() { this.form = this.blankForm(); this.schedModal = true; },
    async saveSchedule() {
      this.saving = true;
      try {
        const r = await executeJson({ function: 'scheduleTelehealthSession', ...this.form }, hms);
        if (r.data && r.data.success) {
          this.schedModal = false;
          await this.loadSessions();
          alert(r.data.message || 'Scheduled.');
        } else {
          alert((r.data && r.data.message) || 'Failed.');
        }
      } catch (e) { console.error(e); }
      finally { this.saving = false; }
    },

    async openConsult(s) {
      this.completeMode = false;
      const r = await executeJson({ function: 'getTelehealthSessionDetail', session_id: s.session_id }, hms);
      if (r.data && r.data.success) {
        this.active = r.data.data.Session || {};
        this.messages = r.data.data.Messages || [];
        this.complete = { ...this.blankComplete(), ...this.pickCompleteFields(this.active) };
        this.consultModal = true;
        this.$nextTick(() => this.scrollChat());
        if (this.pollTimer) clearInterval(this.pollTimer);
        this.pollTimer = setInterval(() => this.refreshMessages(), 5000);
      }
    },
    pickCompleteFields(s) {
      return {
        clinical_notes: s.clinical_notes || '',
        impression: s.impression || '',
        plan_text: s.plan_text || '',
        icd11_codes: s.icd11_codes || '',
        prescription_text: s.prescription_text || '',
        investigations_text: s.investigations_text || '',
        follow_up_date: s.follow_up_date || '',
        refer_in_person: s.refer_in_person || 0,
        referral_facility: s.referral_facility || '',
        connection_quality: s.connection_quality || '',
        technical_notes: s.technical_notes || ''
      };
    },
    async refreshMessages() {
      if (!this.active || !this.active.session_id) return;
      const r = await executeJson({ function: 'getTelehealthSessionDetail', session_id: this.active.session_id }, hms);
      if (r.data && r.data.success) {
        const d = r.data.data || {};
        this.active = d.Session || this.active;
        this.messages = d.Messages || [];
        this.$nextTick(() => this.scrollChat());
      }
    },
    scrollChat() {
      const el = this.$refs.chatBox;
      if (el) el.scrollTop = el.scrollHeight;
    },

    async actOn(action) {
      const r = await executeJson({ function: 'updateTelehealthSession', session_id: this.active.session_id, action }, hms);
      if (r.data && r.data.success) { await this.refreshMessages(); await this.loadSessions(); }
      else alert((r.data && r.data.message) || 'Failed.');
    },
    async cancelSess() {
      const reason = prompt('Cancel reason?');
      if (reason === null) return;
      const r = await executeJson({ function: 'updateTelehealthSession', session_id: this.active.session_id, action: 'cancel', cancel_reason: reason }, hms);
      if (r.data && r.data.success) { await this.refreshMessages(); await this.loadSessions(); }
    },
    async finalizeComplete() {
      this.saving = true;
      try {
        const r = await executeJson({ function: 'updateTelehealthSession', session_id: this.active.session_id, action: 'complete', ...this.complete }, hms);
        if (r.data && r.data.success) { this.completeMode = false; await this.refreshMessages(); await this.loadSessions(); }
        else alert((r.data && r.data.message) || 'Failed.');
      } catch (e) { console.error(e); }
      finally { this.saving = false; }
    },
    async sendChat() {
      if (!this.chatMsg.trim()) return;
      const r = await executeJson({
        function: 'sendTelehealthMessage',
        session_id: this.active.session_id,
        sender_type: this.chatSender,
        sender_name: this.chatSender === 'patient' ? this.active.patient_name : 'You',
        message: this.chatMsg
      }, hms);
      if (r.data && r.data.success) {
        this.chatMsg = '';
        await this.refreshMessages();
      }
    },

    // formatting
    rowClass(s) {
      if (s.urgency === 'emergency' && !['completed', 'cancelled'].includes(s.status)) return 'row-action';
      if (s.status === 'in_progress') return 'row-good';
      if (s.status === 'waiting_room') return 'row-warn';
      return '';
    },
    statusPill(s) { return ({ scheduled:'hms-pill-info', waiting_room:'hms-pill-warn', in_progress:'hms-pill-success', completed:'hms-pill-muted', no_show:'hms-pill-danger', cancelled:'hms-pill-muted' })[s] || ''; },
    urgencyPill(u) { return ({ routine:'hms-pill-info', urgent:'hms-pill-warn', emergency:'hms-pill-danger' })[u] || ''; },
    modePill(m) { return ({ video:'hms-pill-info', audio:'hms-pill-success', chat:'hms-pill-muted', store_forward:'hms-pill-warn' })[m] || ''; },
    modeIcon(m) { return ({ video:'mdi-video', audio:'mdi-phone', chat:'mdi-message-text', store_forward:'mdi-archive-arrow-up' })[m] || 'mdi-message'; },
    qualityClass(q) { return ({ excellent:'st-good', good:'st-good', fair:'st-warn', poor:'st-bad', dropped:'st-bad' })[q] || ''; },
    senderLabel(t) { return ({ provider:'Provider', spoke_clinician:'Spoke', patient:'Patient', system:'System' })[t] || t; },
    ageYears(dob) { if (!dob) return '?'; const d = new Date(dob); if (isNaN(d)) return '?'; return Math.floor((Date.now() - d.getTime()) / 31557600000); },
    formatDt(d) { if (!d) return ''; return new Date(d.replace(' ', 'T')).toLocaleString(); },
    formatTime(d) { if (!d) return ''; return new Date(d.replace(' ', 'T')).toLocaleTimeString([], { hour:'2-digit', minute:'2-digit' }); }
  },
  watch: {
    consultModal(v) { if (!v && this.pollTimer) { clearInterval(this.pollTimer); this.pollTimer = null; } }
  }
};
</script>

<style scoped>
.hms-page-body { padding: 16px 0 32px; }
.hms-back { color: #4a5568; font-size: 20px; text-decoration: none; }
.hms-page-title { font-size: 18px; font-weight: 600; color: #2d3748; }

.hms-tab-row { display: flex; gap: 4px; border-bottom: 1px solid #e2e8f0; margin-bottom: 14px; }
.hms-tab { padding: 10px 16px; border: none; background: none; cursor: pointer; color: #718096; border-bottom: 2px solid transparent; font-size: 14px; }
.hms-tab.active { color: #2b6cb0; border-bottom-color: #2b6cb0; font-weight: 600; }

.hms-stat-row { display: grid; grid-template-columns: repeat(6, 1fr); gap: 12px; margin-bottom: 14px; }
.hms-stat { background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; padding: 12px; text-align: center; }
.hms-stat-num { font-size: 24px; font-weight: 700; color: #1a202c; }
.hms-stat-lbl { font-size: 11px; color: #718096; text-transform: uppercase; letter-spacing: .4px; margin-top: 2px; }
.hms-stat-info { color: #2c5282; } .hms-stat-success { color: #276749; } .hms-stat-warning { color: #c05621; } .hms-stat-danger { color: #c53030; }

.hms-table { width: 100%; border-collapse: collapse; font-size: 13px; background: #fff; }
.hms-table th { background: #f7fafc; padding: 9px; border-bottom: 1px solid #e2e8f0; font-weight: 600; color: #4a5568; text-align: left; }
.hms-table td { padding: 8px 9px; border-bottom: 1px solid #edf2f7; vertical-align: middle; }
.hms-table tr.row-warn   { background: #fffaf0; }
.hms-table tr.row-action { background: #fff5f5; }
.hms-table tr.row-good   { background: #f0fff4; }

.hms-input { width: 100%; padding: 6px 10px; border: 1px solid #cbd5e0; border-radius: 6px; font-size: 13px; }
.hms-label { font-size: 11px; font-weight: 600; color: #4a5568; display: block; margin-bottom: 3px; }

.hms-pill { display: inline-flex; align-items: center; gap: 3px; padding: 2px 8px; border-radius: 10px; font-size: 11px; font-weight: 600; }
.hms-pill-success { background: #c6f6d5; color: #22543d; }
.hms-pill-info    { background: #bee3f8; color: #2a4365; }
.hms-pill-warn    { background: #feebc8; color: #7b341e; }
.hms-pill-danger  { background: #fed7d7; color: #742a2a; }
.hms-pill-muted   { background: #edf2f7; color: #4a5568; }

.hms-modal-bg { position: fixed; inset: 0; background: rgba(0,0,0,.55); display: flex; align-items: center; justify-content: center; z-index: 100; padding: 20px; }
.hms-modal { background: #fff; border-radius: 8px; max-height: 95vh; display: flex; flex-direction: column; overflow: hidden; }
.hms-modal-head { padding: 14px 18px; border-bottom: 1px solid #e2e8f0; display: flex; justify-content: space-between; align-items: center; }
.hms-modal-body { padding: 16px 18px; overflow-y: auto; }
.hms-modal-body.p-0 { padding: 0; }
.hms-modal-foot { padding: 12px 18px; border-top: 1px solid #e2e8f0; display: flex; justify-content: flex-end; gap: 8px; }
.hms-icon-btn { background: none; border: none; cursor: pointer; }

.hms-consult-modal { width: 1100px; max-width: 96vw; }
.hms-consult-grid { display: grid; grid-template-columns: 1.4fr 1fr; gap: 0; min-height: 60vh; }
.hms-consult-left { padding: 16px; border-right: 1px solid #e2e8f0; overflow-y: auto; max-height: 80vh; }
.hms-consult-right { padding: 12px; display: flex; flex-direction: column; max-height: 80vh; background: #f7fafc; }
.hms-section-lbl { font-size: 11px; font-weight: 700; color: #718096; text-transform: uppercase; letter-spacing: .5px; margin-bottom: 6px; }
.hms-consult-section { background: #fafbfc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 12px; }
.hms-video-stub { background: #1a202c; color: #cbd5e0; border-radius: 8px; padding: 32px; text-align: center; }
.hms-video-stub i { color: #4a5568; }

.hms-chat-box { flex: 1; overflow-y: auto; background: #fff; border: 1px solid #e2e8f0; border-radius: 6px; padding: 8px; }
.hms-chat-msg { padding: 6px 10px; border-radius: 8px; margin-bottom: 6px; max-width: 90%; font-size: 13px; }
.hms-chat-msg.sender-provider        { background: #ebf8ff; align-self: flex-start; }
.hms-chat-msg.sender-spoke_clinician { background: #fefcbf; }
.hms-chat-msg.sender-patient         { background: #f0fff4; margin-left: auto; }
.hms-chat-msg.sender-system          { background: #edf2f7; font-style: italic; color: #4a5568; }
.hms-chat-meta { font-size: 10px; color: #718096; margin-bottom: 2px; }
.hms-chat-body { white-space: pre-wrap; }
.hms-chat-input { display: flex; gap: 6px; margin-top: 8px; }

.hms-btn { display: inline-flex; align-items: center; gap: 4px; padding: 7px 12px; border-radius: 6px; border: 1px solid transparent; font-size: 13px; font-weight: 500; cursor: pointer; }
.hms-btn-sm { padding: 4px 10px; font-size: 12px; }
.hms-btn-blue      { background: #2b6cb0; color: #fff; }
.hms-btn-success   { background: #38a169; color: #fff; }
.hms-btn-warning   { background: #d69e2e; color: #fff; }
.hms-btn-secondary { background: #fff; color: #2d3748; border: 1px solid #cbd5e0; }

.st-bad { color: #c53030; font-weight: 600; }
.st-warn { color: #b45309; font-weight: 600; }
.st-good { color: #276749; font-weight: 600; }
.hms-muted { color: #a0aec0; }
.hms-muted-sm { color: #a0aec0; font-size: 11px; }
.hms-pad { padding: 24px; }
</style>
